import axios from 'axios'
import type { Plan, PlanAttachment, PlanComment } from '@/config/types/Plan'
import type { Retailer } from '@/config/types/Retailer'
import type { Group } from '@/config/types/Group'
import {
  mapActionFromApi,
  mapAttachmentFromApi,
  mapCommentFromApi,
  type ApiAction,
  type ApiAttachment,
  type ApiComment,
} from '@/utils/mappers'

type ApiPlan = {
  id: number | string
  title: string
  active: boolean | number
  actions?: ApiAction[]
  retailers?: Retailer[]
  groups?: Group[]
  retailer?: { id: number | string; name?: string; label?: string; code?: string } | null
  attachments?: ApiAttachment[]
  comments?: ApiComment[]
  created_at?: string
}

function mapPlanFromApi(p: ApiPlan): Plan {
  const retailerList: Retailer[] = p.retailers
    ? p.retailers
    : p.retailer
      ? [
          {
            id: p.retailer.id,
            label: p.retailer.label || p.retailer.name || '',
            code: p.retailer.code || '',
            users: [],
          },
        ]
      : []

  const mappedActions = Array.isArray(p.actions)
    ? p.actions.map((action) => {
        return mapActionFromApi(action, true)
      })
    : []

  const attachments: PlanAttachment[] | undefined = Array.isArray(p.attachments)
    ? p.attachments.map(mapAttachmentFromApi)
    : undefined

  const comments: PlanComment[] | undefined = Array.isArray(p.comments)
    ? p.comments.map(mapCommentFromApi)
    : undefined

  return {
    id: p.id,
    title: p.title,
    active: Boolean(p.active),
    actions: mappedActions,
    retailers: retailerList,
    groups: p.groups || [],
    attachments,
    comments,
    createdAt: p.created_at,
  }
}

export default function usePlans() {
  const fetchPlans = async (
    includeCompleted: boolean = false,
    filters?: {
      retailerId?: string | number | null
      regionId?: string | null
      areaIds?: string[]
      search?: string
      overdueOnly?: boolean
      from?: Date | null
      to?: Date | null
      kpiAreas?: string[]
    },
  ): Promise<Plan[]> => {
    const params: Record<string, string> = {}
    if (includeCompleted) {
      params.include_completed = 'true'
    }
    if (filters) {
      if (filters.retailerId) {
        params.retailer_id = String(filters.retailerId)
      }
      if (filters.regionId) {
        params.region_id = filters.regionId
      }
      if (filters.areaIds && filters.areaIds.length > 0) {
        params.area_ids = filters.areaIds.join(',')
      }
      if (filters.search) {
        params.search = filters.search
      }
      if (filters.overdueOnly) {
        params.overdue_only = 'true'
      }
      if (filters.from) {
        params.from = filters.from.toISOString().split('T')[0]
      }
      if (filters.to) {
        params.to = filters.to.toISOString().split('T')[0]
      }
      if (filters.kpiAreas && filters.kpiAreas.length > 0) {
        params.kpi_areas = filters.kpiAreas.join(',')
      }
    }
    const { data } = await axios.get<ApiPlan[]>('/api/plans', { params })
    return (data as ApiPlan[]).map(mapPlanFromApi)
  }

  const createPlan = async (plan: Plan): Promise<Plan> => {
    const payload: {
      title: string
      active: boolean
      retailer_id?: number | null
      retailer_ids?: number[]
      group_ids?: number[]
    } = {
      title: plan.title,
      active: plan.active ?? false,
    }

    if (plan.retailers && plan.retailers.length > 0) {
      payload.retailer_ids = plan.retailers.map((r) => Number(r.id))
    } else {
      payload.retailer_id = null
    }

    if (plan.groups && plan.groups.length > 0) {
      payload.group_ids = plan.groups.map((g) => Number(g.id))
    }

    const { data } = await axios.post<ApiPlan>('/api/plans', payload)
    return mapPlanFromApi(data)
  }

  const updatePlan = async (plan: Plan): Promise<Plan> => {
    const payload: {
      title: string
      active: boolean
      retailer_ids?: number[]
      group_ids?: number[]
    } = {
      title: plan.title,
      active: plan.active,
    }

    if (plan.retailers && plan.retailers.length > 0) {
      payload.retailer_ids = plan.retailers.map((r) => Number(r.id))
    } else {
      payload.retailer_ids = []
    }

    if (plan.groups && plan.groups.length > 0) {
      payload.group_ids = plan.groups.map((g) => Number(g.id))
    } else {
      payload.group_ids = []
    }

    const { data } = await axios.put<ApiPlan>(`/api/plans/${plan.id}`, payload)
    const updatedPlan = mapPlanFromApi(data)

    if (updatedPlan.title !== plan.title || updatedPlan.active !== plan.active) {
      throw new Error('Plan update did not persist correctly. Please try again.')
    }

    return updatedPlan
  }

  const duplicatePlan = async (planId: string | number): Promise<Plan> => {
    const { data } = await axios.post<ApiPlan>(`/api/plans/${planId}/duplicate`)
    return mapPlanFromApi(data)
  }

  const deletePlan = async (planId: string | number): Promise<void> => {
    await axios.delete(`/api/plans/${planId}`)
  }

  const restorePlan = async (planId: string | number): Promise<Plan> => {
    const { data } = await axios.post<ApiPlan>(`/api/plans/${planId}/restore`)
    return mapPlanFromApi(data)
  }

  const fetchTrashedPlans = async (): Promise<Plan[]> => {
    const { data } = await axios.get<ApiPlan[]>('/api/plans/trashed')
    return (data as ApiPlan[]).map(mapPlanFromApi)
  }

  return {
    fetchPlans,
    createPlan,
    updatePlan,
    duplicatePlan,
    deletePlan,
    restorePlan,
    fetchTrashedPlans,
    uploadPlanAttachment,
    downloadPlanAttachment,
    deletePlanAttachment,
    fetchPlanAttachments,
    fetchPlanComments,
    postPlanComment,
    deletePlanComment,
  }
}

export async function uploadPlanAttachment(planId: string | number, file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await axios.post(`/api/plans/${planId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return mapAttachmentFromApi(data)
}

export async function downloadPlanAttachment(
  planId: string | number,
  attachmentId: string | number,
  fileName?: string,
) {
  const response = await axios.get(`/api/plans/${planId}/attachments/${attachmentId}`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName || 'attachment')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export async function deletePlanAttachment(
  planId: string | number,
  attachmentId: string | number,
) {
  await axios.delete(`/api/plans/${planId}/attachments/${attachmentId}`)
}

export async function fetchPlanAttachments(planId: string | number): Promise<PlanAttachment[]> {
  const { data } = await axios.get<ApiAttachment[]>(`/api/plans/${planId}/attachments`)
  return data.map(mapAttachmentFromApi)
}

export async function fetchPlanComments(planId: string | number): Promise<PlanComment[]> {
  const { data } = await axios.get<ApiComment[]>(`/api/plans/${planId}/comments`)
  return data.map(mapCommentFromApi)
}

export async function postPlanComment(
  planId: string | number,
  body: string,
  parentId?: string | number | null,
): Promise<PlanComment> {
  const { data } = await axios.post(`/api/plans/${planId}/comments`, {
    body,
    parent_id: parentId ?? null,
  })
  return mapCommentFromApi(data)
}

export async function deletePlanComment(planId: string | number, commentId: string | number) {
  await axios.delete(`/api/plans/${planId}/comments/${commentId}`)
}
